import { useGetDiscountCodesOnMount } from '@user/store/discountCode/hooks/useGetDiscountCodesOnMount'
import { selectAllDiscountCodes } from '@user/store/discountCode/slice'
import { IDiscountCode } from '@user/store/discountCode/typings'
import {
  ISalesTeam,
  selectAllSalesTeams,
  useGetSalesTeamListOnMount,
} from '@user/store/salesTeam'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Props } from '../DiscountCodeSelect'
import { DensityType } from '@user/components/Opportunities/AddOpportunityPage/typings'

export function useData(props: Props) {
  const { salesTeamId } = props
  const tidalThemeActive = useTidalThemeActive()

  const [value, setValue] = useState<IDiscountCode | null>(null)

  const status = useGetDiscountCodesOnMount()
  const teamStatus = useGetSalesTeamListOnMount({
    includeTeamsByBranchId: true,
  })
  const allDiscountCodes: IDiscountCode[] = useSelector(selectAllDiscountCodes)

  const salesTeams: ISalesTeam[] = useSelector(selectAllSalesTeams)

  const density: DensityType = tidalThemeActive ? 'normal' : 'compact'

  const salesTeam = useMemo(
    () =>
      teamStatus === 'success'
        ? salesTeams.find((x) => x.id == +salesTeamId)
        : undefined,
    [salesTeams, salesTeamId, teamStatus],
  )

  const branchId = salesTeam?.branchId ?? undefined

  const discountCodesForBranch = useMemo(
    () =>
      branchId
        ? allDiscountCodes.filter((x) => x.branchIds.includes(branchId))
        : [],
    [allDiscountCodes, branchId],
  )

  useEffect(() => {
    const matchingDiscount = discountCodesForBranch.find(
      (x) => x.id.toString() === props.code,
    )
    setValue(matchingDiscount ?? null)
  }, [discountCodesForBranch, props.code])

  const description = useMemo(() => {
    if (value) {
      return value.description
    } else {
      return ''
    }
  }, [value])

  const getOptionLabel = useCallback(
    (option: IDiscountCode) => `${option.code} - ${option.name}`,
    [],
  )

  const getId = useCallback((option: IDiscountCode) => {
    return String(option.id)
  }, [])

  const discountDisabled = !salesTeamId || salesTeamId.length == 0

  return {
    value,
    setValue,
    status,
    teamStatus,
    discountCodes: discountCodesForBranch,
    description,
    discountDisabled,
    getOptionLabel,
    getId,
    density,
  } as const
}
export type UseData = ReturnType<typeof useData>
